import { InjectionToken } from "@angular/core";
import { ActivityTarget, AffinityStatus, CustomizationSettingsIdentifier, InterestCategory, Picto } from "@shared/models";

export const CWT_APP_CONFIG = new InjectionToken<CwtAppConfig>('cwt-app-config');

export type CwtAppStayConfig = {
    projection: string;
    storageKey: string;
    useDates: boolean;
    redirectOnPartnerInactiveLoggedIn: string[];
    redirectOnPartnerInactiveLoggedOut: string[];
    redirectOnPartnerActive: string[];
    partnerSearchMode: 'code' | 'list';
}

export type CwtAppLayoutConfig = {
    headerLayout: 'simple' | 'extended';
    headerIcon?: string;
    modalHeaderToolbarColor?: string;
    modalHeaderTitleColor?: string;
}

export type CwtAppActivitiesConfig = {
    enable: boolean;
    defaultPicture?: string;
    canBookEvent: boolean;
    showBookEventOnCard: boolean;
    canRateEvent: boolean;
    showEventHost: boolean;
    forceAppIconOnEventHost: boolean;
    highlightPartnerEvents: boolean;
    redirectOnCardClick: boolean;
    activityTargets: ActivityTarget[];
}

export type CwtAppEnjoymentConfig = {
    enable: boolean;
    defaultPicture?: string;
}

export type CwtAppPrestationConfig = {
    isClickableOnCustomerSpace: boolean;
}

export type CwtAppTipsConfig = {
    enable: boolean;
    defaultPicture?: string;
    redirectOnUrl: boolean;
    showFavorite: boolean;
}

export enum CwtAppAuthBackground {
    Landing = 'landing',
    Login = 'login',
    Register = 'register',
    ForgotPassword = 'forgot-password',
    ResetPassword = 'reset-password',
    EmailValidation = 'email-validation'
}

export type CwtAppAuthConfig = {
    useLogin: boolean;
    loginRoute: string[];
    registerRoute: string[];
    landingRoute: string[];
    forgotPasswordRoute: string[];
    backgrounds?: Partial<Record<CwtAppAuthBackground, string>>;
}

export type CwtAppCardsConfig = {
    enable: boolean;
    defaultPicture?: string;
}

export type CwtAppCustomizableElementConfig = {
    defaultPicture: string;
    defaultTitle: string;
    redirectUrl: string[];
    queryParams?: any;
}

export type CwtAppPartnerConfig = {
    defaultPicture: string;
    defaultCardImage: string;
    customizableElements: Partial<Record<CustomizationSettingsIdentifier, CwtAppCustomizableElementConfig>>;
    useConversation: boolean;
    useInventory: boolean;
    useCustomerSpace: boolean;
    useClientSpace: boolean;
    useNotification: boolean;
    useLocations: boolean;
    maxPicturePerInventoryReport?: number;
    showHomeCardOverlayToolbar: boolean;
    showPartnerLogoInList: boolean;
    pictoIcons?: Record<Picto, string>;
    pictoOrder?: Picto[];
    searchCriteria: 'region' | 'search';
}

export type CwtAppEventConfig = {
    targetPictures: Partial<Record<ActivityTarget, string>>;
    useReservationDelay: boolean;
    maxCapacity?: number;
    eventWarningSubscription?: boolean;
}

export type CwtAppMiscConfig = {
    useUserImageFix: boolean; // Use to check on user image if match old version.
    userImagePath: string; // The path to use in place of the asset url
}

export type CwtAppAffinityConfig = {
    affinityStatusPictures?: Partial<Record<AffinityStatus, string>>;
    interestCategoryPictures?: Partial<Record<InterestCategory, string>>;
    useAffinityFields?: boolean;
}

export type CwtDeeplinkConfig = {
    enable: boolean;
}

export type CwtAppConfig = {
    userDefaultPicture: string;
    deeplink: CwtDeeplinkConfig;
    logoPicture: string;
    landingLogo: string;
    webDomain: string;
    layout: CwtAppLayoutConfig;
    stay: CwtAppStayConfig;
    tips: CwtAppTipsConfig;
    cards: CwtAppCardsConfig;
    enjoyments: CwtAppEnjoymentConfig;
    prestations: CwtAppPrestationConfig;
    activities: CwtAppActivitiesConfig;
    partner: CwtAppPartnerConfig;
    event: CwtAppEventConfig;
    auth: CwtAppAuthConfig;
    affinity?: CwtAppAffinityConfig;
    misc?: CwtAppMiscConfig;
}